import type { ErrorInfo, ReactNode } from "react"
import React, { Component } from "react"

interface Props {
  children: ReactNode
  capsuleType: string
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class CapsuleErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      `Failed to load capsule ${this.props.capsuleType}:`,
      error,
      errorInfo
    )
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>
      }

      return (
        <div className="border border-red-200 bg-red-50 p-6 rounded-lg my-4">
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Failed to load component
          </h3>
          <p className="text-red-600 text-sm mb-2">
            Component type: {this.props.capsuleType}
          </p>
          {process.env.NODE_ENV === "development" && (
            <details className="text-sm text-gray-600">
              <summary className="cursor-pointer text-red-600 mb-2">
                Error details
              </summary>
              <pre className="bg-white p-2 rounded border border-red-200 overflow-auto">
                {this.state.error?.message}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
